import { useEffect, useState } from "react";
import { useStore } from "../../../store";
import Coupon from "../../../components/others/PaymentPopup/Coupon";
import StripeCheckoutForm from "../../../components/others/PaymentPopup/StripeCheckoutForm";
import FreeCheckoutForm from "../../../components/others/PaymentPopup/FreeCheckoutForm";
import TunisianCheckoutForm from "../../../components/others/PaymentPopup/TunisianCheckoutForm";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import env from "../../../config/env";
import { Button, Loading } from "src/components/shared";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const stripePromise = loadStripe(env.REACT_APP_STRIPE_PUBLIC_KEY);

const PaymentPopup = () => {
  const navigate = useNavigate();
  const { courseStore, paymentStore, userStore } = useStore();
  const { id } = useParams();

  const country = localStorage.getItem("country");
  const isTunisian: boolean = localStorage.getItem("country") === "Tunisia";

  const [clientSecret, setClientSecret] = useState(null);
  const [price, setPrice] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [email, setEmail] = useState(userStore?.user?.email || "");

  useEffect(() => {
    loadSession();
  }, []);

  useEffect(() => {
    if (sessionLoaded && price > 0 && !isTunisian) {
      fetchClientSecret();
    }
  }, [price, sessionLoaded]);

  const loadSession = async () => {
    try {
      if (id) {
        await courseStore.getCohortById(id);
        setSessionLoaded(true);
        const session = courseStore?.classDetails;
        let price = session?.price;
        if (session?.newSessionDiscount) {
          price = (session?.price * (100 - session?.newSessionDiscount)) / 100;
        }
        setPrice(price);
      }
      if (!userStore.user) {
        await userStore.getUser();
      }
    } catch (error) {
      toast.error("Error loading session data. Please try again.");
      close();
    }
  };

  const fetchClientSecret = async () => {
    setReloading(true);
    const res = await paymentStore.createStripePaymentIntent(
      price * 100,
      userStore.currency,
      id,
      userStore.user?.id
    );
    if (res?.clientSecret) {
      setClientSecret(res?.clientSecret);
      setReloading(false);
      return res?.clientSecret;
    }
    toast.error("Error while processing your payment. Please try again.");
    close();
  };

  const close = () => {
    navigate("/class/" + id);
  };

  if (price > 0 && (!clientSecret || !stripePromise) && !isTunisian) {
    return <Loading />;
  }

  return (
    <div className="w-full min-h-screen relative">
      <img
        src="/images/blur-bg.png"
        className="absolute top-0 left-0 w-full h-full object-cover z-[-1]"
      />
      <div className="fixed top-5 right-5 sm:right-9 cursor-pointer size-8 sm:size-11">
        <div className="hidden sm:block">
          <Button
            type="pale"
            icon="/images/icons/close-black.svg"
            onClick={close}
            customStyle={{
              borderRadius: "9999px",
              padding: "14px",
            }}
          />
        </div>
        <div className="block sm:hidden">
          <Button
            type="pale"
            icon="/images/icons/close-black.svg"
            onClick={close}
            customStyle={{
              borderRadius: "9999px",
              padding: "8px",
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:gap-2 max-w-[1020px] min-h-screen mx-auto justify-center p-5 sm:px-[60px] lg:px-0 sm:py-0">
        <div className="flex items-center gap-1.5 text-primary-gray-400">
          <span className="body-small">Enrolling from</span>
          <select
            className="caption-text p-1 bg-[#F9F9F9] border border-[#DFDFDF] rounded-full"
            name=""
            id=""
            defaultValue="us"
          >
            <option value="us">{country}</option>
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 lg:gap-5">
          <Coupon email={email} price={price} setPrice={setPrice} />
          {reloading && (
            <div className="flex items-center justify-center w-3/5 h-full bg-white rounded-[30px]">
              <svg
                aria-hidden="true"
                className="w-12 h-12 text-gray-200 animate-spin fill-[#E83C70]"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          )}
          {price <= 0 ? (
            <FreeCheckoutForm
              email={email}
              setEmail={setEmail}
              loading={loading}
              setLoading={setLoading}
            />
          ) : isTunisian ? (
            <TunisianCheckoutForm
              email={email}
              setEmail={setEmail}
              price={price}
            />
          ) : (
            clientSecret &&
            stripePromise &&
            !reloading && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#0570DE",
                      borderRadius: "12px",
                    },
                  },
                }}
              >
                <StripeCheckoutForm
                  clientSecret={clientSecret}
                  email={email}
                  setEmail={setEmail}
                  price={price}
                  loading={loading}
                  setLoading={setLoading}
                />
              </Elements>
            )
          )}
        </div>
        {!isTunisian && userStore?.user && (
          <div className="flex items-center justify-end gap-1.5">
            <span className="body-small text-primary-gray-400">Secured by</span>
            <img src="/images/icons/stripe.svg" />
          </div>
        )}
      </div>
      {/* </>
      )} */}
    </div>
  );
};

export default PaymentPopup;
